import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, Mic, Send, X, RotateCcw, ChevronLeft, Maximize2, Minimize2 } from 'lucide-react';
import { useModel } from '../context/ModelContext';
import { ChatSession, researchChat } from '../services/researchChat';
import './ResearchChatDock.css';
import { readChats, storeChat, SavedChat } from '../services/chatHistory';
import { pageSnapshot, performPageAction } from '../utils/pageAgent';
import { openScenarioWorkspace } from '../utils/scenarioNavigation';

const ResearchMessage = React.lazy(() => import('./ResearchMessage'));

// Browser dictation is optional. Typed messages work on every supported browser.
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type VoiceWindow = Window & { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };

const ResearchChatDock: React.FC = () => {
  const { importChatScenario } = useModel();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [history, setHistory] = useState<SavedChat[]>(readChats);
  const nextToken = useRef<string | null>(history[0]?.token || null);
  const nextDraft = useRef(history[0]?.draft || '');
  const [token, setToken] = useState<string | null>(nextToken.current);
  const [expanded, setExpanded] = useState(false);
  const [conversationTitle, setConversationTitle] = useState('');
  const receipts = useRef(new Map<string, Record<string,unknown>>());
  const executing = useRef(new Set<string>());
  const clientId = useRef(`page-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [listening, setListening] = useState(false);
  const [imported, setImported] = useState<string[]>([]);
  const [generation, setGeneration] = useState(0);
  const recognition = useRef<Recognition | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const epoch = useRef(0);
  const starting = useRef(false);
  const voiceWindow = window as VoiceWindow;
  const Voice = voiceWindow.SpeechRecognition || voiceWindow.webkitSpeechRecognition;

  useEffect(() => {
    epoch.current += 1;
    starting.current = false;
    setSession(null); setToken(nextToken.current); setError(null); setImported([]); setDraft(nextDraft.current); setPending(false);
    recognition.current?.abort();
    setVoiceError(null); setListening(false);
  }, [generation]);

  useEffect(() => {
    if (token) setHistory(storeChat(token, session, draft));
    // Store the small conversation index and draft, never simulation arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, session?.title, draft]);
  useEffect(() => { setConversationTitle(session?.title || 'New conversation'); }, [session?.title]);

  const switchConversation = (saved?: SavedChat) => {
    if (token) storeChat(token, session, draft);
    if (token && session?.status === 'thinking') void researchChat.stop(token).catch(() => {});
    nextToken.current = saved?.token || null;
    nextDraft.current = saved?.draft || '';
    setGeneration(value => value + 1);
  };

  useEffect(() => {
    if (!open || token || starting.current) return;
    const version = epoch.current;
    starting.current = true;
    setPending(true);
    (async () => {
      try {
        const available = await researchChat.availability();
        if (epoch.current !== version) return;
        setEnabled(available.enabled);
        if (!available.enabled) return;
        const next = await researchChat.start();
        if (epoch.current !== version) return;
        setToken(next.token); setSession(next);
      } catch (e) {
        if (epoch.current === version) setError((e as Error).message);
      } finally {
        if (epoch.current === version) { setPending(false); starting.current = false; }
      }
    })();
  }, [open, token, generation]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const next = await researchChat.poll(token);
        if (!cancelled) { setSession(next); setError(null); }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
      if (!cancelled) timer = setTimeout(poll, open ? 1200 : 5000);
    };
    void poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [token, open]);

  useEffect(() => {
    const request = session?.page_request;
    if (!open || !token || !request || executing.current.has(request.id)) return;
    const version = epoch.current;
    let cancelled = false;
    executing.current.add(request.id);
    void (async () => {
      let result = receipts.current.get(request.id);
      let claimed = false;
      try {
        claimed = (await researchChat.claimPage(token, request.id, clientId.current)).claimed;
        if (!claimed || cancelled || version !== epoch.current) return;
        if (!result) {
          const key = `research-action-${request.id}`;
          const prior = sessionStorage.getItem(key);
          if (prior) result = JSON.parse(prior);
          else {
            // Write before acting: after a reload, never repeat an uncertain action.
            sessionStorage.setItem(key, JSON.stringify({error:'This action was already attempted. Inspect the page before retrying.'}));
            result = performPageAction(request);
            await new Promise(resolve => setTimeout(resolve, 80));
            if (!cancelled && version === epoch.current && request.operation !== 'snapshot') result = {...result, page:pageSnapshot()};
            sessionStorage.setItem(key, JSON.stringify(result));
          }
          receipts.current.set(request.id, result!);
        }
        await researchChat.pageResult(token, request.id, clientId.current, result);
      } catch (e) {
        const failure = {error:(e as Error).message};
        receipts.current.set(request.id, failure);
        if (claimed) await researchChat.pageResult(token, request.id, clientId.current, failure).catch(() => {});
      } finally { executing.current.delete(request.id); }
    })();
    return () => { cancelled = true; };
    // Request IDs are immutable; ordinary polling must not cancel an in-flight action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token, session?.page_request?.id]);

  useEffect(() => {
    if (open) input.current?.focus();
    else { recognition.current?.abort(); setListening(false); }
  }, [open]);
  useEffect(() => () => recognition.current?.abort(), []);
  useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [session?.messages, session?.proposal]);

  const busy = pending || !session || ['preparing', 'thinking', 'solving', 'compacting'].includes(session.status);
  const close = () => { setOpen(false); trigger.current?.focus(); };

  const act = async (action: () => Promise<unknown>) => {
    const version = epoch.current;
    setPending(true); setError(null);
    try {
      await action();
      if (token && epoch.current === version) {
        const next = await researchChat.poll(token);
        if (epoch.current === version) setSession(next);
      }
    } catch (e) {
      if (epoch.current === version) setError((e as Error).message);
    } finally {
      if (epoch.current === version) setPending(false);
    }
  };

  const send = () => {
    if (!token || busy || !draft.trim()) return;
    const text = draft.trim();
    const version = epoch.current;
    recognition.current?.stop();
    void act(async () => { await researchChat.send(token, text); if (version === epoch.current) setDraft(''); });
  };

  const dictate = () => {
    if (listening) { recognition.current?.stop(); return; }
    if (!Voice) return;
    setVoiceError(null);
    const rec = new Voice();
    recognition.current = rec;
    const prefix = draft.trim();
    rec.lang = navigator.language || 'en-US';
    rec.continuous = false;
    rec.interimResults = true;
    rec.onresult = event => {
      const transcript = Array.from(event.results).map(result => result[0].transcript).join(' ');
      setDraft(`${prefix}${prefix ? ' ' : ''}${transcript}`.slice(0, 6000));
    };
    rec.onerror = event => { if (event.error !== 'aborted') setVoiceError(`Dictation: ${event.error}. You can still type your message.`); setListening(false); };
    rec.onend = () => setListening(false);
    try { rec.start(); setListening(true); } catch { setVoiceError('Microphone unavailable. Please type your message.'); }
  };

  const showRun = (id: string) => {
    if (!token) return;
    const version = epoch.current;
    void act(async () => {
      const response = await researchChat.result(token, id);
      if (version !== epoch.current) return;
      const run = response.run;
      importChatScenario(response.framework, response.dataset, {
        id: `chat-${id}`, framework: response.framework, dataset: response.dataset, name: run.name, tariffConfig: run.config.tariffConfig,
        kappaConfig: run.config.kappaConfig || undefined,
        result: run.result,
        status: 'solved', createdAt: new Date(),
      });
      setImported(previous => [...previous, id]);
      openScenarioWorkspace(); setOpen(false);
    });
  };

  return <>
    <nav className={`research-dock ${open ? 'research-dock-open' : ''}`} aria-label="Research tools">
      <span className="research-dock-handle" aria-hidden="true"><ChevronLeft size={14} /></span>
      <button ref={trigger} className="research-dock-button" onClick={() => setOpen(value => !value)}
        aria-label="Open research chat" aria-expanded={open} aria-controls="research-chat-panel" title="Research chat">
        <MessageSquare size={22} /><span>Chat</span>
      </button>
    </nav>
    {open && <aside id="research-chat-panel" className={`research-chat${expanded ? ' research-chat-expanded' : ''}`} aria-label="Counterfactual research chat"
      onKeyDown={event => { if (event.key === 'Escape') close(); }}>
      <header className="research-chat-header">
        <div><strong>Research assistant</strong><small>GPT-6 Astra</small></div>
        <button title="New conversation" aria-label="New conversation" disabled={busy && !!session}
          onClick={() => switchConversation()}><RotateCcw size={17} /></button>
        <button aria-label={expanded ? "Restore chat size" : "Expand chat"} onClick={() => setExpanded(value => !value)}>{expanded ? <Minimize2 size={17} /> : <Maximize2 size={17} />}</button>
        <button title="Close chat" aria-label="Close chat" onClick={close}><X size={20} /></button>
      </header>
      <div className="research-session-controls">
        <label>Conversation<select aria-label="Conversation" value={token || ''} onChange={e => switchConversation(history.find(c => c.token === e.target.value))}>
          {!token && <option value="">New conversation</option>}
          {history.map(c => <option key={c.token} value={c.token}>{c.title}</option>)}
        </select></label>
        {token && <input aria-label="Conversation name" value={conversationTitle} maxLength={80}
          onChange={e => setConversationTitle(e.target.value)} onBlur={() => {
            if (conversationTitle.trim() && conversationTitle !== session?.title) void act(() => researchChat.rename(token, conversationTitle));
          }} />}
        <div className="research-session-actions">
          <button disabled={!token || busy} onClick={() => { if (token) void act(() => researchChat.compact(token)); }}>Compact context</button>
          {session?.status === 'thinking' && <button onClick={() => { if (token) void act(() => researchChat.stop(token)); }}>Stop response</button>}
          {session?.usage?.last?.totalTokens != null && <small>{session.usage.last.totalTokens.toLocaleString()} context tokens{session.usage.modelContextWindow ? ` / ${session.usage.modelContextWindow.toLocaleString()}` : ''}</small>}
        </div>
      </div>
      {session?.context_sources && <details className="research-context-sources"><summary>Project context</summary>
        <p>{session.context_sources.join(' · ')}</p><small>Version {session.context_version}</small><small>Prepared from backend sources. Changes refresh before the next response.</small></details>}
      <div ref={scroll} className="research-chat-scroll">
        {enabled === false ? <p>Research chat is not enabled on this server yet. The existing analysis tools are available as usual.</p> : <>
          {!session?.messages.length && <div className="research-chat-welcome">
            <h3>Work with this page</h3>
            <p>Ask me to configure a scenario, display results or explain the model.</p>
            <button disabled={busy} onClick={() => { setDraft('Using CP2015, what happens if the U.S. adds 10 percentage points to import tariffs?'); input.current?.focus(); }}>
              What if the U.S. imposes a 10% tariff?</button>
            <button disabled={busy} onClick={() => { setDraft('Which models and counterfactual scenarios can I choose?'); input.current?.focus(); }}>
              Which scenarios can I run?</button>
          </div>}
          <div role="log" aria-label="Conversation" aria-live="polite" aria-relevant="additions">
            {session?.messages.map(message => <article key={message.id} className={`research-message research-message-${message.role}`}>
              <small>{message.role === 'user' ? 'You' : message.role === 'assistant' ? 'Research assistant' : 'Simulation'}</small>
              {message.role === 'assistant' ? <React.Suspense fallback={<div>{message.text}</div>}>
                <ResearchMessage text={message.text} /></React.Suspense> : <div>{message.text}</div>}
            </article>)}
          </div>
          {session?.proposal && <section className="research-proposal" aria-label="Scenario to confirm">
            <h3>{session.proposal.name}</h3><p>{session.proposal.explanation}</p>
            <p className="research-proposal-model">{session.proposal.framework} · {session.proposal.dataset} · Long-run</p>
            {session.proposal.tariff_rules.map((rule, i) => <dl key={i}>
              <dt>Importer</dt><dd>{rule.importers.join(', ')}</dd>
              <dt>Partners</dt><dd>{rule.exporters.join(', ')}</dd>
              <dt>Sectors</dt><dd>{rule.sectors.join(', ')}</dd>
              <dt>Tariff</dt><dd>{rule.mode === 'Increment' ? `Add ${rule.rate} percentage points` : `Set final rate to ${rule.rate}%`}</dd>
            </dl>)}
            {session.proposal.kappaConfig && <dl>
              {Object.entries(session.proposal.kappaConfig.kappa_S).map(([c, v]) => <React.Fragment key={`S-${c}`}><dt>{c} investor friction</dt><dd>× {v}</dd></React.Fragment>)}
              {Object.entries(session.proposal.kappaConfig.kappa_D).map(([c, v]) => <React.Fragment key={`D-${c}`}><dt>{c} host friction</dt><dd>× {v}</dd></React.Fragment>)}
            </dl>}
            {!!session.proposal.assumptions.length && <ul>{session.proposal.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>}
            <button className="btn-primary" disabled={busy} onClick={() => {
              if (token && session.proposal) void act(() => researchChat.confirm(token, session.proposal!.id));
            }}>Confirm & run simulation</button>
            <button className="btn-outline" disabled={busy} onClick={() => {
              const proposal = session.proposal!;
              openScenarioWorkspace({ id: `draft-${proposal.id}`, framework: proposal.framework, dataset: proposal.dataset,
                name: proposal.name, tariffConfig: proposal.tariffConfig, kappaConfig: proposal.kappaConfig || undefined,
                result: null, status: 'pending', createdAt: new Date() });
              setOpen(false);
            }}>Edit in workspace</button><small>Describe revisions here, or edit a separate draft in the workspace.</small>
          </section>}
          {session?.runs.map(run => <section className="research-run" key={run.id}>
            <strong>{run.config.framework} · {run.name}</strong><small>{run.status === 'running' ? 'Simulation running…' : run.status === 'done' ? 'Simulation complete' : run.error || 'Simulation failed'}</small>
            {run.status === 'done' && <button className="btn-outline" disabled={pending || imported.includes(run.id)} onClick={() => showRun(run.id)}>
              {imported.includes(run.id) ? 'Added to Counterfactual Analysis' : 'Add to existing figures'}</button>}
          </section>)}
          {session?.activity && <p role="status" className="research-chat-status">{session.activity}</p>}
          {session?.status === 'compacting' && <p role="status">Compacting conversation context…</p>}
          {(pending || session?.status === 'preparing' || session?.status === 'thinking' || session?.status === 'solving') &&
            <p className="research-chat-status" role="status">{session?.status === 'solving' ? 'Running the confirmed scenario…' :
              session?.status === 'thinking' ? 'Research assistant is working…' : 'Connecting to simulation API…'}</p>}
        </>}
        {(voiceError || error || session?.error) && <p className="research-chat-error" role="alert">{voiceError || error || session?.error}</p>}
      </div>
      <footer className="research-chat-composer">
        <textarea ref={input} value={draft} maxLength={6000} disabled={enabled === false}
          onChange={event => setDraft(event.target.value)} placeholder="Ask a question or tell me what to do on the page…"
          aria-label="Message to research assistant" rows={3}
          onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(); } }} />
        <div><button className={listening ? 'research-mic-active' : ''} onClick={dictate} disabled={!Voice || busy}
          aria-label={listening ? 'Stop dictation' : 'Dictate a message'} aria-pressed={listening}
          title={Voice ? 'Browser speech recognition; audio may be processed by your browser’s speech service. Review text before sending.' : 'Dictation unavailable in this browser; use keyboard or system dictation.'}>
          <Mic size={18} />{listening ? 'Listening…' : 'Dictate'}</button>
          <button className="research-chat-send" disabled={busy || !draft.trim()} onClick={send} aria-label="Send message"><Send size={18} /></button></div>
        <small>Review dictated text before sending. Simulations require confirmation.</small>
      </footer>
    </aside>}
  </>;
};

export default ResearchChatDock;
